'use client';

import React, { ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';

import { BreadcrumbsSeparator } from '@/public/icons/blog/icons';

type TBreadCrumbProps = {
  homeElement: ReactNode;
  separator?: ReactNode;
  containerClasses?: string;
  listClasses?: string;
  activeClasses?: string;
  capitalizeLinks?: boolean;
  lastLabel?: string;
};

const Breadcrumb = ({ homeElement, lastLabel }: TBreadCrumbProps) => {
  const paths = usePathname();
  const pathNames = paths.split('/').filter((path) => path);

  if (pathNames.includes('admin')) return null;

  return (
    <Wrapper>
      <List itemScope itemType="https://schema.org/BreadcrumbList">
        {['home', ...pathNames].map((link, index) => {
          const href = `/${pathNames.slice(0, index + 1).join('/')}`;
          const itemClasses =
            index !== pathNames.length ? 'active' : 'noactive';
          const itemLink = link
            ? (link[0].toUpperCase() + link.slice(1)).replace(/-/g, ' ')
            : link;
          const displayLabel =
            index === pathNames.length && lastLabel ? lastLabel : itemLink;
          return (
            <React.Fragment key={index}>
              <Item
                className={itemClasses}
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <Link
                  href={index === 0 ? '/' : index === 1 ? homeElement : href}
                  itemProp="item"
                >
                  <span itemProp="name">{displayLabel}</span>
                </Link>
                <meta itemProp="position" content={(index + 1).toString()} />
              </Item>
              {index !== pathNames.length && <BreadcrumbsSeparator />}
            </React.Fragment>
          );
        })}
      </List>
    </Wrapper>
  );
};

export default Breadcrumb;

const Wrapper = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: start;
  width: 100%;
`;

const List = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  gap: 4px;
`;

const Item = styled.li`
  font-family: var(--font-roboto);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  & a {
    text-decoration: none;
  }
  &.home-element {
  }

  &.noactive {
    a {
      color: #999;
      pointer-events: none;
    }
  }

  &.active {
    a {
      color: #206fed;
    }
  }
`;
